import React from 'react';
import { Link } from 'react-router-dom';
import { ORG, AUTHOR } from '@/config/authorProfile';

/**
 * 4-column publisher-style footer. Used on all trust/legal pages and
 * anywhere else we don't want the homepage's oversized sign-off footer.
 *
 * Product / Guides / Company / Policies — mirrors the IA in the readiness
 * plan and gives AdSense a clear "this is a real site" signal.
 */
export default function SiteFooter({ className = '' }) {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className={`border-t border-slate-200 bg-white ${className}`}
    >
      <div className="container mx-auto px-4 sm:px-6 py-10 max-w-6xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Product */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3">
              Product
            </p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-slate-700 hover:text-blue-600">Predict</Link></li>
              <li><Link to="/live" className="text-slate-700 hover:text-blue-600">Live Busiest</Link></li>
              <li><Link to="/insights" className="text-slate-700 hover:text-blue-600">National Insights</Link></li>
              <li><Link to="/reports" className="text-slate-700 hover:text-blue-600">Weekly Crowd Report</Link></li>
              <li><Link to="/thanksgiving-2026-forecast" className="text-slate-700 hover:text-blue-600">Thanksgiving Forecast</Link></li>
              <li><Link to="/christmas-2026-forecast" className="text-slate-700 hover:text-blue-600">Christmas Forecast</Link></li>
              <li><Link to="/widget" className="text-slate-700 hover:text-blue-600">Embed Widget</Link></li>
              <li><Link to="/methodology" className="text-slate-700 hover:text-blue-600">Methodology</Link></li>
            </ul>
          </div>

          {/* Guides */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3">
              Guides
            </p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/airports" className="text-slate-700 hover:text-blue-600">Airport Guides</Link></li>
              <li><Link to="/blog" className="text-slate-700 hover:text-blue-600">Blog</Link></li>
              <li><Link to="/faq" className="text-slate-700 hover:text-blue-600">FAQ</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3">
              Company
            </p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-slate-700 hover:text-blue-600">About</Link></li>
              <li><Link to="/contact" className="text-slate-700 hover:text-blue-600">Contact</Link></li>
              <li><Link to="/masthead" className="text-slate-700 hover:text-blue-600">Masthead</Link></li>
              <li><Link to={`/authors/${AUTHOR.slug}`} className="text-slate-700 hover:text-blue-600">Editor</Link></li>
            </ul>
          </div>

          {/* Policies */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-3">
              Policies
            </p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/editorial-standards" className="text-slate-700 hover:text-blue-600">Editorial Standards</Link></li>
              <li><Link to="/corrections" className="text-slate-700 hover:text-blue-600">Corrections</Link></li>
              <li><Link to="/affiliate-disclosure" className="text-slate-700 hover:text-blue-600">Affiliate Disclosure</Link></li>
              <li><Link to="/privacy" className="text-slate-700 hover:text-blue-600">Privacy</Link></li>
              <li><Link to="/terms" className="text-slate-700 hover:text-blue-600">Terms</Link></li>
            </ul>
          </div>
        </div>

        {/* Affiliate disclosure line — sitewide, FTC 16 CFR Part 255 */}
        <div className="border-t border-slate-100 pt-6 text-xs text-slate-500 leading-relaxed">
          <p className="mb-2">
            <span className="font-semibold text-slate-700">Disclosure:</span>{' '}
            This site contains affiliate links to travel partners (parking,
            credit cards, lounges). If you book or apply through them, we may
            earn a commission at no cost to you. It never influences editorial
            coverage.{' '}
            <Link to="/affiliate-disclosure" className="text-blue-600 hover:underline">
              Read the full disclosure →
            </Link>
          </p>
          <p>
            © {year} {ORG.name}. Operated by {ORG.legalOwner}. Flight data
            provided by FlightAware AeroAPI.
          </p>
        </div>
      </div>
    </footer>
  );
}
